import React, { useEffect, useMemo, useRef, useState } from "react";
import SlideOver from "../../Modal/SlideOver";
import { isBusinessWriteBlocked } from "../../../../Constants/business";
import WriteBlockGate from "../../Business/WriteBlockGate";
import { useTranslation } from "react-i18next";
import {
  cancelAPIRequest,
  cancelStorageRequest,
  getAccountingFileName,
} from "../../../../Utils";
import clsx from "clsx";
import {
  clearTermsError,
  clearTermsStatus,
  requestAcceptTerms,
  requestDocumentExcelAccTransactionUpload,
  requestGetTerms,
  requestGetUser,
  requestListTermsAcceptance,
  requestRemoveTermsAcceptance,
  useAccountingDispatch,
  useAccountingState,
  useAuthDispatch,
  useAuthState,
  useTermsDispatch,
  useTermsState,
  usePeriodLock,
} from "../../../../Context";
import { getPeriodClosedError } from "../../../../Utils";
import PeriodLockBanner from "../PeriodLockBanner";
import PeriodClosedNotice from "../PeriodClosedNotice";
import { useNavigate } from "react-router-dom";
import { downloadData, getUrl } from "aws-amplify/storage";
import FormError from "../../Form/FormError";
import {
  ChevronLeftIcon,
  ExclamationIcon,
  RefreshIcon,
  SparklesIcon,
  XIcon,
} from "@heroicons/react/outline";
import Button from "../../Button";
import { Field, Label, Switch } from "@headlessui/react";
import Markdown from "react-markdown";
import { rawMarkdownRehypePlugins } from "../../../../Utils/markdownSanitize";
import remarkGfm from "remark-gfm";
import { ClipboardListIcon } from "@heroicons/react/solid";
import { useRemoteConfig } from "../../../../Context/firebase/context";
import Badge from "../../../Badge";
import MaintenanceAlert from "../../../MaintenanceAlert";
import DownloadTemplateCard from "../../DownloadTemplateCard";
import UploadFile from "../../UploadFile";

const TERMS_ID = "accounting-upload-ai-v1";

const TransactionUploadModal = ({ open, onClose }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAccountingDispatch();
  const termsDispatch = useTermsDispatch();
  const userDispatch = useAuthDispatch();
  const {
    accounting_import_detection: importDetectionConfig,
    loading: configLoading,
  } = useRemoteConfig();

  const [selectedFile, setSelectedFile] = useState(null);
  const [isDragActive, setIsDragActive] = useState(false);
  const [fieldErrorFile, setfieldErrorFile] = useState({
    status: false,
    message: "",
  });
  const [downloading, setDownloading] = useState(false);
  const [showPremiumWarning, setShowPremiumWarning] = useState(false);
  const [aiEnabled, setaiEnabled] = useState(false);
  const [showTermsView, setShowTermsView] = useState(false);
  const [downloadDocError, setDownloadDocError] = useState(null);
  const [importDetectionEnabled, setimportDetectionEnabled] = useState(true);
  const [
    importDetectionMaintenanceVisible,
    setimportDetectionMaintenanceVisible,
  ] = useState(false);
  const [importDetectionStatus, setimportDetectionStatus] = useState("");
  const fileInputRef = useRef(null);
  const downloadRef = useRef(null);
  const formContainerRef = useRef(null);

  const {
    uploadError,
    progressUpload,
    storageRequest,
    uploadLoading,
    uploadSuccess,
    uploadStatusError,
    excelUpload,
  } = useAccountingState();
  const { user } = useAuthState();
  const writeBlocked = isBusinessWriteBlocked(user?.mainBusiness);
  const { refetch: refetchPeriod } = usePeriodLock(user?.mainBusiness?.id);
  const {
    request: termsRequest,
    terms,
    termsLoading,
    termsError,
    termsAcceptances,
    acceptTermsLoading,
    acceptTermsError,
    removeAcceptanceLoading,
    statusAccept,
  } = useTermsState();

  const termsContent = terms?.contents?.items
    ?.find((item) => item.language === i18n.language)
    ?.content?.replace(/# (.*?)(?=\\n|$)/g, "<h1>$1</h1>");

  const { isAccepted, termAcceptance } = useMemo(() => {
    const currentAcceptance = termsAcceptances?.find(
      (acceptance) => acceptance.termsAndConditions?.id === TERMS_ID
    );
    return {
      termAcceptance: currentAcceptance,
      isAccepted: Boolean(currentAcceptance?.id),
    };
  }, [termsAcceptances]);

  useEffect(() => {
    if (isAccepted && statusAccept && showTermsView) {
      setShowTermsView(false);
      clearTermsStatus(termsDispatch);
    }
  }, [isAccepted, showTermsView, statusAccept, termsDispatch]);

  useEffect(() => {
    return () => {
      if (storageRequest) {
        cancelStorageRequest(storageRequest);
      }
    };
  }, [storageRequest]);

  useEffect(() => {
    if (uploadSuccess || uploadStatusError) {
      if (excelUpload?.importID && aiEnabled) {
        navigate(`/accounting-transaction/draft/${excelUpload.importID}`);
      } else {
        navigate(`/accounting-transaction/import?id=${user?.mainBusiness?.id}`);
      }
    }
  }, [
    uploadSuccess,
    user.mainBusiness?.id,
    uploadStatusError,
    excelUpload,
    aiEnabled,
    navigate,
  ]);

  useEffect(() => {
    if (user?.username) {
      requestGetUser(userDispatch, { sub: user.username });
      requestListTermsAcceptance(termsDispatch, {
        termsID: TERMS_ID,
        userID: user.id,
      });
    }
  }, [user?.username, termsDispatch, userDispatch, user?.id]);

  useEffect(() => {
    onLoadTerms();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [importDetectionStatus, termsDispatch]);

  const scrollTo = (position) => {
    formContainerRef.current?.scrollIntoView({
      block: position,
      behavior: "smooth",
    });
  };

  const scrollToBottom = () => scrollTo("end");
  const scrollToTop = () => scrollTo("start");

  const handleNoPremium = () => {
    setShowPremiumWarning(true);
    scrollToTop();
  };

  const downloadBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename || "download";
    const clickHandler = () => {
      setTimeout(() => {
        URL.revokeObjectURL(url);
        a.removeEventListener("click", clickHandler);
      }, 150);
    };
    a.addEventListener("click", clickHandler, false);
    a.click();
    return a;
  };

  const downloadFile = async (key) => {
    try {
      setDownloading(true);

      const request = downloadData({
        path: `public/${key}`,
      });

      downloadRef.current = request;
      const result = await request.result;

      const fileName = getAccountingFileName(key);

      downloadBlob(result.body, fileName);
    } catch (downloadError) {
      if (downloadError.message !== "canceled") {
        await handleDownloadError(key, downloadError);
      }
    } finally {
      setDownloading(false);
    }
  };

  const handleDownloadError = async (key) => {
    try {
      const urlResult = await getUrl({
        path: `public/${key}`,
        options: {
          useAccelerateEndpoint: true,
        },
      });
      const signedURL = urlResult.url.href;
      window.open(signedURL);
    } catch (signedURLError) {
      setDownloadDocError(signedURLError);
    }
  };

  const handleDownloadTemplate = () => {
    // Assuming you have a specific key for the template file
    downloadFile("template/accounting_transaction_template.xlsx");
  };

  const handleFileValidation = (file) => {
    const maxSize = 100 * 1024; // 100 KB
    const allowedType =
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

    const validations = [
      {
        condition: file.type !== allowedType,
        message: "transactions.errors.fileType",
      },
      {
        condition: file.size > maxSize,
        message: "transactions.errors.fileSize",
      },
      {
        condition: file.name.length >= 120,
        message: "transactions.errors.fileCharacter",
      },
    ];

    const failedValidation = validations.find((val) => val.condition);

    return failedValidation
      ? { isValid: false, message: failedValidation.message }
      : { isValid: true, message: "" };
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (e.currentTarget.contains(e.relatedTarget)) {
      return;
    }
    setIsDragActive(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);

    const file = e.dataTransfer ? e.dataTransfer.files[0] : e.target.files[0];
    const validation = handleFileValidation(file);

    if (validation.isValid) {
      setfieldErrorFile({ status: false, message: "" });
      setSelectedFile(file);

      scrollToBottom();
    } else {
      setfieldErrorFile({ status: true, message: validation.message });
      e.target.value = "";
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    const validation = handleFileValidation(file);

    if (validation.isValid) {
      setfieldErrorFile({ status: false, message: "" });
      setSelectedFile(file);

      scrollToBottom();
    } else {
      setfieldErrorFile({ status: true, message: validation.message });
      e.target.value = "";
    }
  };

  const handleFileSelect = () => {
    fileInputRef.current.click();
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!selectedFile) {
      setfieldErrorFile({
        status: true,
        message: "transactions.errors.fileNone",
      });
      return;
    }

    if (user?.membership !== "PREMIUM" && aiEnabled) {
      handleNoPremium();
      return;
    }

    requestDocumentExcelAccTransactionUpload(dispatch, {
      useDetection: aiEnabled,
      businessID: user?.mainBusiness?.id,
      file: selectedFile,
    });
  };

  const onChangeAcceptance = () => {
    if (isAccepted) {
      requestRemoveTermsAcceptance(termsDispatch, {
        acceptanceID: termAcceptance.id,
      });
    } else {
      requestAcceptTerms(termsDispatch, {
        termsID: TERMS_ID,
        userID: user.id,
      });
    }
  };

  const onLoadTerms = () => {
    if (importDetectionStatus !== "DISABLED") {
      requestGetTerms(termsDispatch, { id: TERMS_ID });
    }
  };

  const handleShowTerms = () => {
    if (Boolean(termsError)) {
      onLoadTerms();
    } else {
      setShowTermsView(true);
    }
  };

  const onCloseTerms = () => {
    setShowTermsView(false);
    clearTermsError(termsDispatch);
    if (termsRequest) {
      cancelAPIRequest(termsRequest);
    }
  };

  // Premium warning card component
  const renderPremiumWarningCard = () => (
    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
      <div className="flex">
        <div className="flex-shrink-0">
          <ExclamationIcon
            className="h-5 w-5 text-yellow-400"
            aria-hidden="true"
          />
        </div>
        <div className="ml-3">
          <h3 className="text-sm font-medium text-yellow-800">
            {t("premium.alert.title")}
          </h3>
          <div className="mt-2 text-sm text-yellow-700">
            <p>{t("premium.alert.description")}</p>
          </div>
          <div className="mt-4">
            <Button
              type="button"
              size="sm"
              variant="primary"
              onClick={() => {
                navigate("/subscription");
                setShowPremiumWarning(false);
              }}
            >
              {t("premium.action.subscribe")}
            </Button>
          </div>
        </div>
        <div className="ml-3">
          <button
            type="button"
            onClick={() => {
              setShowPremiumWarning(false);
            }}
          >
            <XIcon className="w-5 h-5 text-gray-400 hover:text-gray-500" />
          </button>
        </div>
      </div>
    </div>
  );

  const termsSwitchDisabled = termsError || !termsContent;

  const isButtonDisabled =
    importDetectionStatus !== "DISABLED" &&
    !isAccepted &&
    (aiEnabled || termsSwitchDisabled);

  const isUnavailable =
    importDetectionStatus === "OFFLINE" ||
    importDetectionStatus === "SCHEDULED_OFFLINE" ||
    importDetectionStatus === "SCHEDULED_ACTIVE";

  const renderMaintenanceBadge = () => {
    const badgeValue = isUnavailable ? "unavailable" : "maintenance";
    const badgeStyle = isUnavailable ? "secondary" : "alert";

    return (
      <div className="flex items-center">
        <Badge title={badgeValue} type={badgeStyle} />
      </div>
    );
  };

  // Main content view with upload form
  const renderMainContent = () => (
    <form ref={formContainerRef} id="upload-form" onSubmit={handleSubmit}>
      <WriteBlockGate>
      <MaintenanceAlert
        isVisible={importDetectionMaintenanceVisible}
        onClose={() => setimportDetectionMaintenanceVisible(false)}
        onChangeAvailability={(available) =>
          setimportDetectionEnabled(available)
        }
        onChangeStatus={(status) => setimportDetectionStatus(status)}
        configValue={importDetectionConfig}
      />
      {showPremiumWarning && renderPremiumWarningCard()}
      {/* A spreadsheet has no date picker to constrain, so this upload is the
          one write that cannot be prevented at the input. Stating the boundary
          before the file is chosen is the only prevention available. */}
      <PeriodLockBanner
        businessID={user?.mainBusiness?.id}
        className="mb-6"
      />
      <PeriodClosedNotice
        className="mb-6"
        error={uploadError}
        onRefresh={refetchPeriod}
      />
      <FormError
        className="mb-6"
        error={
          getPeriodClosedError(uploadError) ? downloadDocError : downloadDocError || uploadError
        }
      />
      <div className="flex flex-col space-y-6 pb-6">
        <DownloadTemplateCard
          description="transactions.modal.description.template"
          onClick={handleDownloadTemplate}
          loading={downloading}
          disabled={downloading}
        />
        {importDetectionStatus !== "DISABLED" && (
          <div className="border border-blue-300 bg-blue-100 rounded-lg p-4">
            <div className="flex flex-col gap-2">
              <div>
                <p className="text-sm text-blue-600 mb-3">
                  {t("transactions.modal.description.aiDetection")}
                </p>
                <div className="flex justify-between">
                  <label className="block text-sm font-medium text-gray-700">
                    {t("transactions.fields.settings")}
                  </label>
                  <button
                    type="button"
                    onClick={() =>
                      navigate(
                        `/accounting-transaction/draft?business=${user.mainBusiness?.id}`
                      )
                    }
                    className="text-sm font-medium text-blue-600"
                  >
                    {t("transactions.fields.history")}
                  </button>
                </div>
                <Field className="flex items-center py-2 px-3 mt-1 border border-gray-300 rounded-md bg-white">
                  <Switch
                    checked={aiEnabled}
                    disabled={!importDetectionEnabled && isUnavailable}
                    onChange={() => {
                      if (importDetectionEnabled) {
                        setaiEnabled(!aiEnabled);
                      } else {
                        setimportDetectionMaintenanceVisible(true);
                        scrollToTop();
                      }
                    }}
                    className={clsx(
                      aiEnabled ? "bg-blue-600" : "bg-gray-200",
                      "relative inline-flex flex-shrink-0 h-6 w-11 disabled:cursor-not-allowed border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    )}
                  >
                    <span
                      className={clsx(
                        aiEnabled ? "translate-x-5" : "translate-x-0",
                        !importDetectionEnabled && isUnavailable
                          ? "bg-gray-300"
                          : "bg-white",
                        "pointer-events-none relative inline-block h-5 w-5 rounded-full shadow transform ring-0 transition ease-in-out duration-200"
                      )}
                    >
                      <span
                        className={clsx(
                          aiEnabled
                            ? "opacity-0 ease-out duration-100"
                            : "opacity-100 ease-in duration-200",
                          "absolute inset-0 h-full w-full flex items-center justify-center transition-opacity"
                        )}
                        aria-hidden="true"
                      >
                        <svg
                          className="h-3 w-3 text-gray-400"
                          fill="none"
                          viewBox="0 0 12 12"
                        >
                          <path
                            d="M4 8l2-2m0 0l2-2M6 6L4 4m2 2l2 2"
                            stroke="currentColor"
                            strokeWidth={2}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </span>
                      <span
                        className={clsx(
                          aiEnabled
                            ? "opacity-100 ease-in duration-200"
                            : "opacity-0 ease-out duration-100",
                          "absolute inset-0 h-full w-full flex items-center justify-center transition-opacity"
                        )}
                        aria-hidden="true"
                      >
                        <svg
                          className="h-3 w-3 text-blue-600"
                          fill="currentColor"
                          viewBox="0 0 12 12"
                        >
                          <path d="M3.707 5.293a1 1 0 00-1.414 1.414l1.414-1.414zM5 8l-.707.707a1 1 0 001.414 0L5 8zm4.707-3.293a1 1 0 00-1.414-1.414l1.414 1.414zm-7.414 2l2 2 1.414-1.414-2-2-1.414 1.414zm3.414 2l4-4-1.414-1.414-4 4 1.414 1.414z" />
                        </svg>
                      </span>
                    </span>
                  </Switch>
                  <Label as="span" className="flex flex-1 justify-between ml-3">
                    <span className="text-sm text-gray-700">
                      {t("transactions.placeholders.settings")}
                    </span>
                    {importDetectionEnabled ? (
                      <div>
                        <SparklesIcon className="h-6 w-6 text-yellow-500" />
                      </div>
                    ) : (
                      renderMaintenanceBadge()
                    )}
                  </Label>
                </Field>
              </div>
              <div>
                <div className="flex justify-between">
                  <label className="block text-sm font-medium text-gray-700">
                    {t("transactions.fields.approval")}
                  </label>
                </div>
                {termsLoading ||
                acceptTermsLoading ||
                removeAcceptanceLoading ? (
                  <div className="animate-pulse flex items-center justify-between py-2 px-3 mt-1 border border-gray-300 rounded-md bg-white gap-4">
                    <div className="flex flex-1 gap-2 items-center">
                      <div className="h-6 w-12 bg-gray-300 rounded-xl" />
                      <div className="h-4 w-1/2 bg-gray-300 rounded-md" />
                    </div>
                    <div className="h-6 w-6 bg-gray-300 rounded-md " />
                  </div>
                ) : (
                  <Field className="flex items-center py-2 px-3 mt-1 border border-gray-300 rounded-md bg-white gap-2">
                    <Switch
                      checked={isAccepted}
                      disabled={termsSwitchDisabled}
                      onChange={onChangeAcceptance}
                      className={clsx(
                        isAccepted ? "bg-blue-600" : "bg-gray-200",
                        "relative inline-flex flex-shrink-0 h-6 w-11 disabled:cursor-not-allowed border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                      )}
                    >
                      <span
                        className={clsx(
                          isAccepted ? "translate-x-5" : "translate-x-0",
                          termsSwitchDisabled ? "bg-gray-300" : "bg-white",
                          "pointer-events-none relative inline-block h-5 w-5 rounded-full shadow transform ring-0 transition ease-in-out duration-200"
                        )}
                      >
                        <span
                          className={clsx(
                            isAccepted
                              ? "opacity-0 ease-out duration-100"
                              : "opacity-100 ease-in duration-200",
                            "absolute inset-0 h-full w-full flex items-center justify-center transition-opacity"
                          )}
                          aria-hidden="true"
                        >
                          <svg
                            className="h-3 w-3 text-gray-400"
                            fill="none"
                            viewBox="0 0 12 12"
                          >
                            <path
                              d="M4 8l2-2m0 0l2-2M6 6L4 4m2 2l2 2"
                              stroke="currentColor"
                              strokeWidth={2}
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </span>
                        <span
                          className={clsx(
                            isAccepted
                              ? "opacity-100 ease-in duration-200"
                              : "opacity-0 ease-out duration-100",
                            "absolute inset-0 h-full w-full flex items-center justify-center transition-opacity"
                          )}
                          aria-hidden="true"
                        >
                          <svg
                            className="h-3 w-3 text-blue-600"
                            fill="currentColor"
                            viewBox="0 0 12 12"
                          >
                            <path d="M3.707 5.293a1 1 0 00-1.414 1.414l1.414-1.414zM5 8l-.707.707a1 1 0 001.414 0L5 8zm4.707-3.293a1 1 0 00-1.414-1.414l1.414 1.414zm-7.414 2l2 2 1.414-1.414-2-2-1.414 1.414zm3.414 2l4-4-1.414-1.414-4 4 1.414 1.414z" />
                          </svg>
                        </span>
                      </span>
                    </Switch>
                    <div className="flex flex-1 justify-between">
                      <Label>
                        <span
                          className={`text-sm ${
                            termsError ? "text-red-600" : "text-gray-700"
                          }`}
                        >
                          {t(
                            termsError
                              ? "transactions.errors.loadTerms"
                              : "transactions.placeholders.approval"
                          )}
                        </span>
                      </Label>
                      <button type="button" onClick={handleShowTerms}>
                        {termsError ? (
                          <RefreshIcon className="h-6 w-6 text-gray-700" />
                        ) : (
                          <ClipboardListIcon className="h-6 w-6 text-blue-500" />
                        )}
                      </button>
                    </div>
                  </Field>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Upload File */}
        <UploadFile
          title="transactions.action.upload"
          file={selectedFile}
          setFile={setSelectedFile}
          handleFileSelect={handleFileSelect}
          handleDragEnter={handleDragEnter}
          handleDragLeave={handleDragLeave}
          handleDragOver={handleDragOver}
          handleDrop={handleDrop}
          handleFileChange={handleFileChange}
          fileInputRef={fileInputRef}
          isDragActive={isDragActive}
          fieldErrorFile={fieldErrorFile}
          uploadLoading={uploadLoading}
          storageRequest={storageRequest}
          progressUpload={progressUpload}
        />
      </div>
      </WriteBlockGate>
    </form>
  );

  // Terms and conditions view
  const renderTermsContent = () => (
    <div className="flex flex-col h-full">
      {/* Terms header with back button */}
      <div className="py-3 border-b border-gray-200 flex items-center">
        <button
          type="button"
          className="text-gray-500 hover:text-gray-700 mr-2 flex items-center"
          onClick={onCloseTerms}
        >
          <ChevronLeftIcon className="w-5 h-5" />
          <span>{t("common.back")}</span>
        </button>
      </div>

      {/* Terms content */}
      <div className="flex-grow overflow-auto py-4">
        <FormError className="mb-4" error={acceptTermsError} />

        {termsLoading ? (
          <div className="animate-pulse space-y-4">
            <div className="h-4 bg-gray-200 rounded w-3/4"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2"></div>
            <div className="h-4 bg-gray-200 rounded w-5/6"></div>
            <div className="h-4 bg-gray-200 rounded w-2/3"></div>
          </div>
        ) : (
          <div className="text-sm text-gray-700">
            <Markdown
              rehypePlugins={rawMarkdownRehypePlugins}
              remarkPlugins={[remarkGfm]}
              components={{
                p({ children }) {
                  return <p className="mb-2 last:mb-0">{children}</p>;
                },
                h1({ children }) {
                  return <h1 className="text-bold text-2xl">{children}</h1>;
                },
                h2({ children }) {
                  return <h2 className="text-bold text-xl">{children}</h2>;
                },
                h3({ children }) {
                  return <h3 className="text-bold text-lg">{children}</h3>;
                },
                ol({ children }) {
                  return (
                    <ol className="list-inside list-decimal">{children}</ol>
                  );
                },
                ul({ children }) {
                  return <ul className="list-inside list-disc">{children}</ul>;
                },
                li({ children }) {
                  return (
                    <li className="mb-2 list-item list-inside">{children}</li>
                  );
                },
                blockquote({ children }) {
                  return (
                    <blockquote className="relative border-s-4 border-gray-800 bg-slate-200 pl-2 ps-4 sm:ps-6">
                      {children}
                    </blockquote>
                  );
                },
              }}
            >
              {termsContent || t("termsNotAvailable")}
            </Markdown>
          </div>
        )}
      </div>
    </div>
  );

  // Dynamic slide over content based on state
  const slideOverContent = showTermsView
    ? renderTermsContent()
    : renderMainContent();

  // Dynamic slide over title based on state
  const slideOverTitle = showTermsView
    ? "transactions.terms.title"
    : "transactions.modal.title.upload";

  // Dynamic slide over footer based on state
  const slideOverFooter = (
    <div className="flex justify-end space-x-3">
      <Button
        type="button"
        variant="secondary"
        onClick={() => {
          if (showTermsView) {
            onCloseTerms();
          } else {
            onClose();
          }
        }}
      >
        {t("common.cancel")}
      </Button>
      {showTermsView ? (
        <Button
          loading={acceptTermsLoading || configLoading}
          disabled={
            acceptTermsLoading || isAccepted || !termsContent || configLoading
          }
          onClick={() => {
            requestAcceptTerms(termsDispatch, {
              termsID: TERMS_ID,
              userID: user.id,
            });
          }}
          variant="primary"
        >
          {isAccepted ? t("transactions.terms.statusTerms") : t("common.agree")}
        </Button>
      ) : (
        <Button
          type="submit"
          loading={uploadLoading || configLoading}
          disabled={
            writeBlocked || uploadLoading || isButtonDisabled || configLoading
          }
          form="upload-form"
          variant="primary"
        >
          {t("common.continue")}
        </Button>
      )}
    </div>
  );

  return (
    <SlideOver
      open={open}
      onClose={showTermsView ? () => setShowTermsView(false) : onClose}
      title={slideOverTitle}
      footer={slideOverFooter}
    >
      {slideOverContent}
    </SlideOver>
  );
};

export default TransactionUploadModal;
